/**
 * Anonymous telemetry — sends shadow UUID + device fingerprint to backend.
 * Silent: never throws, never blocks UI.
 */
import { getShadowId } from './localStore';
import { getDeviceFingerprint } from './deviceFingerprint';
import { getCachedGeo } from './geolocation';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace('http://', 'https://');

let _deviceFp = null;
const ensureFp = async () => {
  if (_deviceFp) return _deviceFp;
  try { _deviceFp = await getDeviceFingerprint(); }
  catch { _deviceFp = null; }
  return _deviceFp;
};

const post = async (path, body) => {
  try {
    const url = `${BACKEND_URL}${path}`;
    const fp = await ensureFp();
    const geo = getCachedGeo();
    const payload = JSON.stringify({
      shadow_id: getShadowId(),
      device_fp: fp,
      geo: geo?.granted ? { lat: geo.lat, lng: geo.lng } : null,
      ...body,
    });
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch((err) => {
      if (process.env.NODE_ENV !== 'production') console.debug('[telemetry] fetch failed:', err?.message);
    });
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('[telemetry] post failed:', err?.message);
  }
};

export const telemetry = {
  registerShadow: () => post('/api/telemetry/shadow-profile', {}),
  matchCompleted: ({ presetId, durationSec, sideA, sideB, winner }) =>
    post('/api/telemetry/match-completed', {
      preset_id: presetId,
      duration_sec: durationSec,
      side_a: sideA,
      side_b: sideB,
      winner,
    }),
  streakPing: ({ dow, weeksInARow }) =>
    post('/api/telemetry/streak-ping', { dow, weeks: weeksInARow }),
};

// Read public ad config (no auth)
export const fetchPublicAdConfig = async () => {
  try {
    const url = `${BACKEND_URL}/api/telemetry/ad-config-public`;
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('[telemetry] ad-config fetch failed:', err?.message);
    return null;
  }
};
